import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Users, Plus, Calendar, MessageCircle, Send, Clock, MapPin,
  ChevronLeft, Trophy, Layers, Settings, Shield, Image, Video,
  CheckCircle2, XCircle, AlertCircle, UserPlus, Film, Camera, Upload, Play,
  Tv, Radio, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPE_STYLES = {
  practice: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-800', icon: '🏀' },
  game: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-800', icon: '🏟️' },
  tournament: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-800', icon: '🏆' },
  film_session: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-800', icon: '🎬' },
  other: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', border: 'border-zinc-700', icon: '📋' },
};

const ROLE_COLORS = {
  program_admin: 'text-amber-400 bg-amber-500/10 border-amber-800',
  coach: 'text-cyan-400 bg-cyan-500/10 border-cyan-800',
  parent: 'text-zinc-400 bg-zinc-500/10 border-zinc-700',
  super_admin: 'text-red-400 bg-red-500/10 border-red-800',
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const ProgramDashboard = () => {
  const { slug } = useParams();
  const { token, user } = useAuth();
  const [program, setProgram] = useState(null);
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [myRole, setMyRole] = useState(null);

  // Add team/member modals
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', sport: '', season: 'Spring 2026', age_group: '' });
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', role: 'parent', email: '', jersey_number: '', position: '' });
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', event_type: 'game', start_time: '', location: '', opponent: '', notes: '' });
  const [mediaItems, setMediaItems] = useState([]);
  const [showUploadMedia, setShowUploadMedia] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCategory, setUploadCategory] = useState('photo');
  const [uploading, setUploading] = useState(false);
  const [gameSessions, setGameSessions] = useState([]);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [newSession, setNewSession] = useState({ title: '', video_url: '', video_type: 'custom', description: '', game_id: '' });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchProgram = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/programs/by-slug/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setProgram(data);
        return data;
      }
    } catch { /* ignore */ }
    return null;
  }, [slug]);

  const fetchTeams = useCallback(async (progId) => {
    try {
      const res = await fetch(`${API_URL}/api/programs/${progId}/teams`);
      if (res.ok) {
        const data = await res.json();
        setTeams(data);
        if (data.length > 0 && !selectedTeam) setSelectedTeam(data[0]);
      }
    } catch { /* ignore */ }
  }, [selectedTeam]);

  const fetchMembers = useCallback(async (progId) => {
    try {
      const res = await fetch(`${API_URL}/api/programs/${progId}/members`);
      if (res.ok) setMembers(await res.json());
    } catch { /* ignore */ }
  }, []);

  const fetchSchedule = useCallback(async (progId) => {
    try {
      const [upRes, pastRes] = await Promise.all([
        fetch(`${API_URL}/api/programs/${progId}/schedule?limit=20&upcoming=true`),
        fetch(`${API_URL}/api/programs/${progId}/schedule?limit=10&upcoming=false`),
      ]);
      const upcoming = upRes.ok ? await upRes.json() : [];
      const past = pastRes.ok ? await pastRes.json() : [];
      // Deduplicate by id, upcoming first
      const seen = new Set();
      const all = [];
      for (const e of [...upcoming, ...past]) {
        if (!seen.has(e.id)) { seen.add(e.id); all.push(e); }
      }
      setSchedule(all);
    } catch { /* ignore */ }
  }, []);

  const fetchMessages = useCallback(async (progId) => {
    try {
      const res = await fetch(`${API_URL}/api/programs/${progId}/messages?limit=30`, { headers });
      if (res.ok) setMessages(await res.json());
    } catch { /* ignore */ }
  }, [token]);

  const fetchMedia = useCallback(async (progId) => {
    try {
      const res = await fetch(`${API_URL}/api/programs/${progId}/media-feed?limit=30`);
      if (res.ok) {
        const data = await res.json();
        setMediaItems(data.media || []);
      }
    } catch { /* ignore */ }
  }, []);

  const fetchGameSessions = useCallback(async (progId) => {
    try {
      const res = await fetch(`${API_URL}/api/programs/${progId}/game-hub`);
      if (res.ok) setGameSessions(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const prog = await fetchProgram();
      if (prog) {
        await Promise.all([
          fetchTeams(prog.id),
          fetchMembers(prog.id),
          fetchSchedule(prog.id),
          fetchMessages(prog.id),
          fetchMedia(prog.id),
          fetchGameSessions(prog.id),
        ]);
        // Check my role
        if (user) {
          try {
            const myProgs = await fetch(`${API_URL}/api/programs/my-programs`, { headers }).then(r => r.json());
            const mine = myProgs.find(p => p.id === prog.id);
            if (mine) setMyRole(mine.my_role);
            else if (user.role === 'admin' || user.role === 'super_admin') setMyRole('super_admin');
          } catch { /* ignore */ }
        }
      }
      setLoading(false);
    };
    load();
  }, [slug, token]);

  const canManage = myRole && ['program_admin', 'super_admin', 'coach'].includes(myRole);

  const sendMessage = async () => {
    if (!chatMessage.trim() || !program) return;
    try {
      const res = await fetch(`${API_URL}/api/programs/${program.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          content: chatMessage.trim(),
          team_id: selectedTeam?.id,
          message_type: 'text',
        }),
      });
      if (res.ok) {
        setChatMessage('');
        fetchMessages(program.id);
      }
    } catch { toast.error('Failed to send'); }
  };

  const addTeam = async () => {
    if (!newTeam.name || !program) return;
    try {
      const res = await fetch(`${API_URL}/api/programs/${program.id}/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ ...newTeam, sport: newTeam.sport || program.sport }),
      });
      if (res.ok) {
        toast.success('Team created');
        setShowAddTeam(false);
        setNewTeam({ name: '', sport: '', season: 'Spring 2026', age_group: '' });
        fetchTeams(program.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Network error'); }
  };

  const addMember = async () => {
    if (!newMember.name || !program) return;
    try {
      const res = await fetch(`${API_URL}/api/programs/${program.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ ...newMember, team_ids: selectedTeam ? [selectedTeam.id] : [] }),
      });
      if (res.ok) {
        toast.success('Member added');
        setShowAddMember(false);
        setNewMember({ name: '', role: 'parent', email: '', jersey_number: '', position: '' });
        fetchMembers(program.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Network error'); }
  };

  const addEvent = async () => {
    if (!newEvent.title || !newEvent.start_time || !program) return;
    try {
      const isoTime = new Date(newEvent.start_time).toISOString();
      const res = await fetch(`${API_URL}/api/programs/${program.id}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ ...newEvent, start_time: isoTime, team_id: selectedTeam?.id }),
      });
      if (res.ok) {
        toast.success('Event added');
        setShowAddEvent(false);
        setNewEvent({ title: '', event_type: 'game', start_time: '', location: '', opponent: '', notes: '' });
        fetchSchedule(program.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Network error'); }
  };

  const uploadMedia = async () => {
    if (!uploadFile || !program) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const uploadRes = await fetch(`${API_URL}/api/uploads/program-media/${program.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!uploadRes.ok) {
        toast.error('Upload failed');
        setUploading(false);
        return;
      }
      const uploadData = await uploadRes.json();
      const isVideo = uploadFile.type.startsWith('video/');
      await fetch(`${API_URL}/api/programs/${program.id}/media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          title: uploadTitle || uploadFile.name,
          url: uploadData.serve_url,
          thumbnail_url: uploadData.serve_url,
          media_type: isVideo ? 'video' : 'photo',
          category: uploadCategory,
          team_id: selectedTeam?.id,
        }),
      });
      toast.success('Media uploaded');
      setShowUploadMedia(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadCategory('photo');
      fetchMedia(program.id);
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const createGameSession = async () => {
    if (!newSession.title || !program) return;
    try {
      const body = { ...newSession };
      if (!body.video_url) delete body.video_url;
      if (!body.game_id) delete body.game_id;
      const res = await fetch(`${API_URL}/api/programs/${program.id}/game-hub`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Watch session created');
        setShowCreateSession(false);
        setNewSession({ title: '', video_url: '', video_type: 'custom', description: '', game_id: '' });
        fetchGameSessions(program.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Network error'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-500 text-lg mb-4">Program not found</p>
          <Link to="/teams" className="text-cyan-400 hover:underline text-sm">Back to Programs</Link>
        </div>
      </div>
    );
  }

  const color = program.primary_color || '#06b6d4';
  const teamMembers = selectedTeam
    ? members.filter(m => m.team_ids?.includes(selectedTeam.id))
    : members;

  const TABS = [
    { id: 'overview', label: 'Overview', icon: Layers },
    { id: 'game-hub', label: 'Game Hub', icon: Tv },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'roster', label: 'Roster', icon: Users },
    { id: 'media', label: 'Media', icon: Image },
    { id: 'chat', label: 'Chat', icon: MessageCircle },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Program Header */}
      <div className="border-b border-zinc-800" style={{ borderBottomColor: `${color}33` }}>
        <div className="max-w-6xl mx-auto px-4 py-5">
          <Link to="/teams" className="text-zinc-500 hover:text-zinc-300 text-xs flex items-center gap-1 mb-3" data-testid="back-to-programs">
            <ChevronLeft className="w-3 h-3" /> All Programs
          </Link>
          <div className="flex items-start gap-3 sm:gap-4">
            <Avatar className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl shrink-0">
              <AvatarFallback className="rounded-xl text-base sm:text-lg font-black text-white" style={{ backgroundColor: color }}>
                {program.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2">
                <h1 className="text-base sm:text-2xl font-bold text-white leading-tight break-words" data-testid="program-name">{program.name}</h1>
                {canManage && (
                  <Link to={`/${slug}/admin`} className="shrink-0 mt-0.5">
                    <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 h-7 w-7 p-0" data-testid="program-settings-btn">
                      <Settings className="w-3.5 h-3.5" />
                    </Button>
                  </Link>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500 mt-1">
                {program.sport && <Badge variant="secondary" className="text-[10px] px-1.5 py-0" style={{ backgroundColor: `${color}20`, color, borderColor: `${color}44` }}>{program.sport}</Badge>}
                {program.city && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {program.city}, {program.state}</span>}
                <span className="flex items-center gap-0.5"><Layers className="w-3 h-3" /> {teams.length} teams</span>
                <span className="flex items-center gap-0.5"><Users className="w-3 h-3" /> {members.length} members</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-zinc-800 sticky top-0 z-10 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-0.5 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-current text-white'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
                style={activeTab === tab.id ? { color } : {}}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Team Selector */}
        {teams.length > 0 && (
          <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
            {teams.map(team => (
              <button
                key={team.id}
                onClick={() => setSelectedTeam(team)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap border ${
                  selectedTeam?.id === team.id
                    ? 'bg-zinc-800 text-white border-zinc-600'
                    : 'bg-transparent text-zinc-500 border-zinc-800 hover:border-zinc-600'
                }`}
                data-testid={`team-pill-${team.id}`}
              >
                {team.name}
                {team.record && team.record !== '0-0' && (
                  <span className="text-[10px] text-zinc-500 font-mono">{team.record}</span>
                )}
              </button>
            ))}
            {canManage && (
              <button
                onClick={() => setShowAddTeam(true)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-cyan-400 border border-dashed border-cyan-800 hover:bg-cyan-900/20 transition-colors"
                data-testid="add-team-btn"
              >
                <Plus className="w-3 h-3" /> Team
              </button>
            )}
          </div>
        )}

        {/* ========== OVERVIEW TAB ========== */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Teams */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-semibold text-base flex items-center gap-2">
                  <Layers className="w-4 h-4" style={{ color }} /> Teams
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {teams.map(team => (
                  <Card
                    key={team.id}
                    className={`bg-zinc-900 border-zinc-800 cursor-pointer transition-all ${selectedTeam?.id === team.id ? 'ring-1' : 'hover:border-zinc-700'}`}
                    style={selectedTeam?.id === team.id ? { '--tw-ring-color': color } : {}}
                    onClick={() => setSelectedTeam(team)}
                    data-testid={`team-card-${team.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-semibold text-sm">{team.name}</h3>
                        <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">
                          {team.age_group || team.sport || program.sport}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-zinc-500">
                        {team.season && <span>{team.season}</span>}
                        {team.record && team.record !== '0-0' && (
                          <span className="flex items-center gap-1">
                            <Trophy className="w-3 h-3" style={{ color }} /> {team.record}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> {team.member_count || 0}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Upcoming Schedule */}
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-semibold text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4" style={{ color }} /> Upcoming
                </h2>
                <button onClick={() => setActiveTab('schedule')} className="text-xs text-cyan-400 hover:underline">View All</button>
              </div>
              {schedule.length === 0 ? (
                <p className="text-zinc-600 text-sm py-4">No upcoming events</p>
              ) : (
                <div className="space-y-2">
                  {schedule.slice(0, 4).map(event => {
                    const style = EVENT_TYPE_STYLES[event.event_type] || EVENT_TYPE_STYLES.other;
                    return (
                      <Card key={event.id} className={`bg-zinc-900 border-zinc-800`} data-testid={`event-${event.id}`}>
                        <CardContent className="p-3 flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-base ${style.bg}`}>
                            {style.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-sm font-medium truncate">{event.title}</span>
                              <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${style.text} ${style.border}`}>
                                {event.event_type.replace('_', ' ')}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-0.5">
                              <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {formatDate(event.start_time)} at {formatTime(event.start_time)}</span>
                              {event.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {event.location}</span>}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sidebar - Chat preview + Recent Members */}
            <div className="space-y-4">
              {/* Quick Chat */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" style={{ color }} /> Team Chat
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                    {messages.length === 0 ? (
                      <p className="text-zinc-600 text-xs py-4 text-center">No messages yet</p>
                    ) : (
                      messages.slice(-5).map(msg => (
                        <div key={msg.id} className="flex gap-2">
                          <Avatar className="h-6 w-6 shrink-0">
                            <AvatarFallback className="text-[10px] bg-zinc-800 text-zinc-400">
                              {msg.sender_name?.[0]?.toUpperCase() || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] font-medium text-zinc-300">{msg.sender_name}</span>
                              <span className="text-[9px] text-zinc-600">{formatTime(msg.created_at)}</span>
                            </div>
                            <p className="text-xs text-zinc-400">{msg.content}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {token && (
                    <div className="flex gap-2">
                      <Input
                        value={chatMessage}
                        onChange={e => setChatMessage(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && sendMessage()}
                        placeholder="Type a message..."
                        className="bg-zinc-800 border-zinc-700 text-white text-xs h-8"
                        data-testid="chat-input"
                      />
                      <Button size="sm" onClick={sendMessage} className="h-8 w-8 p-0" style={{ backgroundColor: color }} data-testid="send-message-btn">
                        <Send className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Roster Quick View */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                      <Users className="w-4 h-4" style={{ color }} /> Roster
                    </h3>
                    <button onClick={() => setActiveTab('roster')} className="text-xs text-cyan-400 hover:underline">View All</button>
                  </div>
                  <div className="space-y-2">
                    {teamMembers.slice(0, 5).map(m => (
                      <div key={m.id} className="flex items-center gap-2">
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarFallback className="text-[10px] bg-zinc-800 text-zinc-400">
                            {m.name?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs text-white font-medium truncate block">{m.name}</span>
                          <span className="text-[10px] text-zinc-500">
                            {m.jersey_number ? `${String(m.jersey_number).startsWith('#') ? m.jersey_number : '#' + m.jersey_number} ` : ''}{m.position || m.role}
                          </span>
                        </div>
                      </div>
                    ))}
                    {teamMembers.length > 5 && (
                      <p className="text-[10px] text-zinc-600 text-center">+{teamMembers.length - 5} more</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ========== GAME HUB TAB ========== */}
        {activeTab === 'game-hub' && (
          <div>
            {/* Live Sessions */}
            {gameSessions.filter(s => s.status === 'live').length > 0 && (
              <div className="mb-5">
                <h3 className="text-red-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Radio className="w-3 h-3 animate-pulse" /> Live Now
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {gameSessions.filter(s => s.status === 'live').map(s => (
                    <Link key={s.id} to={`/${slug}/watch/${s.id}`} data-testid={`session-card-${s.id}`}>
                      <Card className="bg-zinc-900 border-red-900/50 hover:border-red-700/70 transition-all cursor-pointer group">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-start justify-between mb-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                                <Radio className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 animate-pulse" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-white font-semibold text-xs sm:text-sm group-hover:text-red-300 transition-colors truncate">{s.title}</p>
                                {s.team_name && <p className="text-zinc-500 text-[10px] sm:text-xs">{s.team_name}</p>}
                              </div>
                            </div>
                            <Badge className="bg-red-500/20 text-red-400 border-red-700 text-[10px] shrink-0 ml-1">LIVE</Badge>
                          </div>
                          {s.description && <p className="text-zinc-500 text-[10px] sm:text-xs mb-1.5 line-clamp-1">{s.description}</p>}
                          <div className="flex items-center justify-between">
                            <span className="text-zinc-600 text-[10px] flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {s.message_count || 0}</span>
                            <span className="text-red-400 text-xs flex items-center gap-1 group-hover:underline">Watch <ExternalLink className="w-3 h-3" /></span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Start a Watch Party Section — Always visible for admins */}
            {canManage && (
              <div className="mb-5">
                <h3 className="text-white text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Tv className="w-3.5 h-3.5" style={{ color }} /> Start a Watch Party
                </h3>

                {/* Option 1: Pick from scheduled games */}
                {schedule.filter(e => e.event_type === 'game' || e.event_type === 'tournament').length > 0 && (
                  <div className="mb-3">
                    <p className="text-zinc-400 text-xs mb-2">Pick a game from the schedule:</p>
                    <div className="space-y-2">
                      {schedule.filter(e => (e.event_type === 'game' || e.event_type === 'tournament') && e.status !== 'completed').slice(0, 5).map(game => (
                        <Card key={game.id} className="bg-zinc-900/60 border-zinc-800 hover:border-zinc-600 transition-all cursor-pointer group" data-testid={`game-pick-${game.id}`} onClick={() => {
                          setNewSession({ title: game.title, video_url: '', video_type: 'custom', description: `Watch ${game.title} together!`, game_id: game.id });
                          setShowCreateSession(true);
                        }}>
                          <CardContent className="p-3 flex items-center gap-3">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                              <Trophy className="w-4 h-4 text-amber-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs sm:text-sm font-medium truncate group-hover:text-zinc-300">{game.title}</p>
                              <div className="flex items-center gap-2 text-[10px] text-zinc-500 mt-0.5">
                                <span className="flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" /> {new Date(game.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                                {game.location && <span className="flex items-center gap-0.5 truncate"><MapPin className="w-2.5 h-2.5" /> {game.location}</span>}
                              </div>
                            </div>
                            <span className="text-[10px] font-medium shrink-0 px-2 py-1 rounded-full" style={{ backgroundColor: `${color}15`, color }}>Watch</span>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Option 2: Paste a URL */}
                <div className="mb-3">
                  <p className="text-zinc-400 text-xs mb-2">Or paste a stream/video URL:</p>
                  <Card className="bg-zinc-900/60 border-zinc-800">
                    <CardContent className="p-3">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          value={newSession.video_url}
                          onChange={e => setNewSession(s => ({ ...s, video_url: e.target.value }))}
                          placeholder="https://youtube.com/watch?v=... or stream URL"
                          className="bg-zinc-800 border-zinc-700 text-white text-xs flex-1"
                          data-testid="quick-url-input"
                        />
                        <Button
                          size="sm"
                          disabled={!newSession.video_url}
                          onClick={() => {
                            if (!newSession.title) setNewSession(s => ({ ...s, title: 'Live Watch Party' }));
                            setShowCreateSession(true);
                          }}
                          className="text-xs text-white shrink-0"
                          style={{ backgroundColor: color }}
                          data-testid="quick-url-go-btn"
                        >
                          <Tv className="w-3 h-3 mr-1" /> Go Live
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Create Session Detail Form (shown after picking a game or URL) */}
            {showCreateSession && (
              <Card className="bg-zinc-900 border-zinc-700 mb-4" data-testid="create-session-form">
                <CardContent className="p-4">
                  <h3 className="text-white font-semibold text-sm mb-3">Finalize Watch Session</h3>
                  <div className="space-y-2 mb-3">
                    <Input value={newSession.title} onChange={e => setNewSession(s => ({ ...s, title: e.target.value }))} placeholder="Session Title *" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="session-title-input" />
                    <Input value={newSession.description} onChange={e => setNewSession(s => ({ ...s, description: e.target.value }))} placeholder="Description (optional)" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="session-desc-input" />
                    <Input value={newSession.video_url} onChange={e => setNewSession(s => ({ ...s, video_url: e.target.value }))} placeholder="Video/Stream URL (paste link)" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="session-url-input" />
                    <select value={newSession.video_type} onChange={e => setNewSession(s => ({ ...s, video_type: e.target.value }))} className="w-full h-9 px-2 rounded-md bg-zinc-800 border border-zinc-700 text-white text-xs" data-testid="session-type-select">
                      <option value="custom">Direct URL</option>
                      <option value="youtube">YouTube</option>
                      <option value="cloudflare">Cloudflare Stream</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setShowCreateSession(false); setNewSession({ title: '', video_url: '', video_type: 'custom', description: '', game_id: '' }); }} className="text-xs border-zinc-700 text-zinc-400 flex-1">Cancel</Button>
                    <Button size="sm" onClick={createGameSession} disabled={!newSession.title} className="text-xs text-white flex-1" style={{ backgroundColor: color }} data-testid="submit-session-btn">Create & Go Live</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Past / Ended Sessions */}
            {gameSessions.filter(s => s.status !== 'live').length > 0 && (
              <div>
                <h3 className="text-zinc-500 text-xs font-semibold uppercase tracking-wider mb-2">Past Sessions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {gameSessions.filter(s => s.status !== 'live').map(s => (
                    <Link key={s.id} to={`/${slug}/watch/${s.id}`} data-testid={`session-card-${s.id}`}>
                      <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-600 transition-all cursor-pointer group">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-start justify-between mb-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.status === 'scheduled' ? 'bg-amber-500/10' : 'bg-zinc-800'}`}>
                                {s.status === 'scheduled' ? <Clock className="w-4 h-4 text-amber-400" /> : <Play className="w-4 h-4 text-zinc-500" />}
                              </div>
                              <div className="min-w-0">
                                <p className="text-white font-medium text-xs sm:text-sm group-hover:text-zinc-300 transition-colors truncate">{s.title}</p>
                                {s.team_name && <p className="text-zinc-500 text-[10px]">{s.team_name}</p>}
                              </div>
                            </div>
                            <Badge variant="outline" className={`text-[10px] shrink-0 ${s.status === 'scheduled' ? 'text-amber-400 border-amber-800' : 'text-zinc-500 border-zinc-700'}`}>
                              {s.status === 'scheduled' ? 'UPCOMING' : 'ENDED'}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-zinc-600 text-[10px] flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {s.message_count || 0}</span>
                            <span className="text-cyan-400 text-xs flex items-center gap-1 group-hover:underline">{s.status === 'ended' ? 'Replay' : 'Join'} <ExternalLink className="w-3 h-3" /></span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state — no sessions and not admin */}
            {gameSessions.length === 0 && !canManage && (
              <div className="text-center py-16">
                <Tv className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">No watch sessions available</p>
                <p className="text-zinc-600 text-xs mt-1">Check back when a game is live</p>
              </div>
            )}
          </div>
        )}

        {/* ========== SCHEDULE TAB ========== */}
        {activeTab === 'schedule' && (
          <div>
            {canManage && (
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" style={{ color }} /> Schedule
                </h2>
                <Button size="sm" onClick={() => setShowAddEvent(true)} className="text-xs text-white" style={{ backgroundColor: color }} data-testid="add-event-btn">
                  <Plus className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Add Event</span><span className="inline sm:hidden">Add</span>
                </Button>
              </div>
            )}

            {/* Add Event Form */}
            {showAddEvent && (
              <Card className="bg-zinc-900 border-zinc-700 mb-4" data-testid="add-event-form">
                <CardContent className="p-4">
                  <h3 className="text-white font-semibold text-sm mb-3">New Event</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                    <Input value={newEvent.title} onChange={e => setNewEvent(ev => ({ ...ev, title: e.target.value }))} placeholder="Event Title *" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-title-input" />
                    <select value={newEvent.event_type} onChange={e => setNewEvent(ev => ({ ...ev, event_type: e.target.value }))} className="h-9 px-2 rounded-md bg-zinc-800 border border-zinc-700 text-white text-xs" data-testid="event-type-select">
                      <option value="game">Game</option>
                      <option value="practice">Practice</option>
                      <option value="tournament">Tournament</option>
                      <option value="film_session">Film Session</option>
                      <option value="other">Other</option>
                    </select>
                    <Input type="datetime-local" value={newEvent.start_time} onChange={e => setNewEvent(ev => ({ ...ev, start_time: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-start-input" />
                    <Input value={newEvent.location} onChange={e => setNewEvent(ev => ({ ...ev, location: e.target.value }))} placeholder="Location" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-location-input" />
                    <Input value={newEvent.opponent} onChange={e => setNewEvent(ev => ({ ...ev, opponent: e.target.value }))} placeholder="Opponent (if game)" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-opponent-input" />
                    <Input value={newEvent.notes} onChange={e => setNewEvent(ev => ({ ...ev, notes: e.target.value }))} placeholder="Notes" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-notes-input" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowAddEvent(false)} className="text-xs border-zinc-700 text-zinc-400">Cancel</Button>
                    <Button size="sm" onClick={addEvent} className="text-xs text-white" style={{ backgroundColor: color }} data-testid="submit-event-btn">Add Event</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {schedule.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">No events scheduled</p>
                {canManage && <p className="text-zinc-600 text-xs mt-1">Click "Add Event" to create your first event</p>}
              </div>
            ) : (
              <div className="space-y-2">
                {schedule.map(event => {
                  const style = EVENT_TYPE_STYLES[event.event_type] || EVENT_TYPE_STYLES.other;
                  const rsvpCount = event.rsvps ? Object.keys(event.rsvps).length : 0;
                  const isCompleted = event.status === 'completed';
                  return (
                    <Card key={event.id} className={`bg-zinc-900 border-zinc-800 ${isCompleted ? 'opacity-75' : ''}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-xl ${style.bg}`}>
                          {style.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-medium">{event.title}</span>
                            <Badge variant="outline" className={`text-[10px] ${style.text} ${style.border}`}>
                              {event.event_type.replace('_', ' ')}
                            </Badge>
                            {isCompleted && event.result && (
                              <Badge className={`text-[10px] px-1.5 py-0 ${event.result?.startsWith('W') ? 'bg-emerald-500/20 text-emerald-400 border-emerald-800' : 'bg-red-500/20 text-red-400 border-red-800'}`}>
                                {event.result}
                              </Badge>
                            )}
                            {isCompleted && !event.result && (
                              <Badge variant="outline" className="text-[10px] text-zinc-500 border-zinc-700">Final</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-zinc-500">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(event.start_time)}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(event.start_time)}</span>
                            {event.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.location}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] text-zinc-600">{rsvpCount} RSVP</span>
                          {token && (
                            <div className="flex gap-1">
                              <button
                                onClick={async () => {
                                  await fetch(`${API_URL}/api/programs/${program.id}/schedule/${event.id}/rsvp?status=going`, {
                                    method: 'POST', headers,
                                  });
                                  toast.success('RSVP: Going!');
                                  fetchSchedule(program.id);
                                }}
                                className="p-1.5 rounded-md bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                                data-testid={`rsvp-going-${event.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  await fetch(`${API_URL}/api/programs/${program.id}/schedule/${event.id}/rsvp?status=not_going`, {
                                    method: 'POST', headers,
                                  });
                                  toast.info('RSVP: Not going');
                                  fetchSchedule(program.id);
                                }}
                                className="p-1.5 rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                                data-testid={`rsvp-not-going-${event.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ========== ROSTER TAB ========== */}
        {activeTab === 'roster' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold">
                {selectedTeam ? `${selectedTeam.name} Roster` : 'All Members'} ({teamMembers.length})
              </h2>
              {canManage && (
                <Button size="sm" onClick={() => setShowAddMember(true)} className="text-xs" style={{ backgroundColor: color }} data-testid="add-member-btn">
                  <UserPlus className="w-3 h-3 mr-1" /> Add Member
                </Button>
              )}
            </div>

            {/* Add Member Modal */}
            {showAddMember && (
              <Card className="bg-zinc-900 border-zinc-700 mb-4" data-testid="add-member-form">
                <CardContent className="p-4">
                  <h3 className="text-white font-semibold text-sm mb-3">Add Member</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                    <Input value={newMember.name} onChange={e => setNewMember(m => ({ ...m, name: e.target.value }))} placeholder="Name *" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="member-name-input" />
                    <Input value={newMember.email} onChange={e => setNewMember(m => ({ ...m, email: e.target.value }))} placeholder="Email" className="bg-zinc-800 border-zinc-700 text-white text-xs" />
                    <select value={newMember.role} onChange={e => setNewMember(m => ({ ...m, role: e.target.value }))} className="h-9 px-2 rounded-md bg-zinc-800 border border-zinc-700 text-white text-xs" data-testid="member-role-select">
                      <option value="parent">Parent/Fan</option>
                      <option value="coach">Coach</option>
                      <option value="program_admin">Program Admin</option>
                    </select>
                    <Input value={newMember.jersey_number} onChange={e => setNewMember(m => ({ ...m, jersey_number: e.target.value }))} placeholder="Jersey #" className="bg-zinc-800 border-zinc-700 text-white text-xs" />
                    <Input value={newMember.position} onChange={e => setNewMember(m => ({ ...m, position: e.target.value }))} placeholder="Position" className="bg-zinc-800 border-zinc-700 text-white text-xs" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowAddMember(false)} className="text-xs border-zinc-700 text-zinc-400">Cancel</Button>
                    <Button size="sm" onClick={addMember} className="text-xs text-white" style={{ backgroundColor: color }} data-testid="submit-member-btn">Add</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {teamMembers.map(m => (
                <Card key={m.id} className="bg-zinc-900 border-zinc-800" data-testid={`member-card-${m.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarFallback className="text-sm font-bold" style={{ backgroundColor: `${color}30`, color }}>
                        {m.jersey_number ? (String(m.jersey_number).startsWith('#') ? m.jersey_number : `#${m.jersey_number}`) : m.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium truncate">{m.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {m.position && <span className="text-[10px] text-zinc-500">{m.position}</span>}
                        <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${ROLE_COLORS[m.role] || ROLE_COLORS.parent}`}>
                          {m.role === 'program_admin' ? 'Admin' : m.role}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ========== CHAT TAB ========== */}
        {activeTab === 'chat' && (
          <div className="max-w-2xl">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg">
              {/* Messages */}
              <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto" data-testid="chat-messages">
                {messages.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-8">No messages yet. Start the conversation!</p>
                ) : (
                  messages.map(msg => (
                    <div key={msg.id} className="flex gap-3">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarFallback className="text-xs bg-zinc-800 text-zinc-400">
                          {msg.sender_name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{msg.sender_name}</span>
                          <Badge variant="outline" className={`text-[8px] px-1 py-0 ${ROLE_COLORS[msg.sender_role] || ''}`}>
                            {msg.sender_role === 'program_admin' ? 'Admin' : msg.sender_role === 'super_admin' ? 'Staff' : msg.sender_role}
                          </Badge>
                          <span className="text-[10px] text-zinc-600">{formatDate(msg.created_at)} {formatTime(msg.created_at)}</span>
                        </div>
                        <p className="text-sm text-zinc-300 mt-0.5">{msg.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {/* Input */}
              {token ? (
                <div className="border-t border-zinc-800 p-3 flex gap-2">
                  <Input
                    value={chatMessage}
                    onChange={e => setChatMessage(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="bg-zinc-800 border-zinc-700 text-white flex-1"
                    data-testid="chat-tab-input"
                  />
                  <Button onClick={sendMessage} style={{ backgroundColor: color }} data-testid="chat-tab-send-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="border-t border-zinc-800 p-3 text-center">
                  <Link to="/login" className="text-cyan-400 text-sm hover:underline">Sign in to chat</Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== MEDIA TAB ========== */}
        {activeTab === 'media' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold flex items-center gap-2">
                <Image className="w-4 h-4" style={{ color }} /> Media
              </h2>
              <div className="flex items-center gap-2">
                <Link to={`/${slug}/media`} className="text-xs text-cyan-400 hover:underline" data-testid="view-all-media-link">View Full Gallery</Link>
                {canManage && (
                  <Button size="sm" onClick={() => setShowUploadMedia(true)} className="text-xs text-white" style={{ backgroundColor: color }} data-testid="upload-media-btn">
                    <Upload className="w-3 h-3 mr-1" /> Upload
                  </Button>
                )}
              </div>
            </div>

            {/* Upload Form */}
            {showUploadMedia && (
              <Card className="bg-zinc-900 border-zinc-700 mb-4" data-testid="upload-media-form">
                <CardContent className="p-4">
                  <h3 className="text-white font-semibold text-sm mb-3">Upload Media</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                    <Input
                      type="file"
                      accept="image/*,video/*"
                      onChange={e => setUploadFile(e.target.files?.[0] || null)}
                      className="bg-zinc-800 border-zinc-700 text-white text-xs"
                      data-testid="media-file-input"
                    />
                    <Input
                      value={uploadTitle}
                      onChange={e => setUploadTitle(e.target.value)}
                      placeholder="Title (optional)"
                      className="bg-zinc-800 border-zinc-700 text-white text-xs"
                      data-testid="media-title-input"
                    />
                    <select
                      value={uploadCategory}
                      onChange={e => setUploadCategory(e.target.value)}
                      className="h-9 px-2 rounded-md bg-zinc-800 border border-zinc-700 text-white text-xs"
                      data-testid="media-category-select"
                    >
                      <option value="photo">Photo</option>
                      <option value="highlight">Highlight</option>
                      <option value="full_game">Full Game</option>
                    </select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setShowUploadMedia(false); setUploadFile(null); }} className="text-xs border-zinc-700 text-zinc-400">Cancel</Button>
                    <Button size="sm" onClick={uploadMedia} disabled={!uploadFile || uploading} className="text-xs text-white" style={{ backgroundColor: color }} data-testid="submit-media-btn">
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {mediaItems.length === 0 ? (
              <div className="text-center py-16">
                <Image className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">No media yet</p>
                {canManage && <p className="text-zinc-600 text-xs mt-1">Upload photos and videos to get started</p>}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {mediaItems.map(item => {
                  const isVideo = item.media_type === 'video';
                  const catMeta = {
                    highlight: { label: 'Highlight', cls: 'text-amber-400 bg-amber-500/10 border-amber-800' },
                    full_game: { label: 'Full Game', cls: 'text-cyan-400 bg-cyan-500/10 border-cyan-800' },
                    photo: { label: 'Photo', cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-800' },
                  }[item.category] || { label: item.category, cls: 'text-zinc-400 bg-zinc-500/10 border-zinc-700' };
                  return (
                    <Card key={item.id} className="bg-zinc-900 border-zinc-800 overflow-hidden group cursor-pointer hover:border-zinc-600 transition-colors" data-testid={`media-item-${item.id}`}>
                      <div className="aspect-video bg-zinc-800 relative">
                        {item.thumbnail_url || item.url ? (
                          isVideo ? (
                            <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                              <Play className="w-8 h-8 text-zinc-500 group-hover:text-white transition-colors" />
                            </div>
                          ) : (
                            <img src={item.url} alt={item.title} className="w-full h-full object-cover" />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {isVideo ? <Film className="w-8 h-8 text-zinc-600" /> : <Camera className="w-8 h-8 text-zinc-600" />}
                          </div>
                        )}
                        <Badge variant="outline" className={`absolute top-2 left-2 text-[9px] ${catMeta.cls}`}>
                          {catMeta.label}
                        </Badge>
                      </div>
                      <CardContent className="p-2">
                        <p className="text-white text-xs font-medium truncate">{item.title || 'Untitled'}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {item.team_name && <span className="text-[10px] text-zinc-500">{item.team_name}</span>}
                          {item.created_at && <span className="text-[10px] text-zinc-600">{new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Add Team Modal */}
        {showAddTeam && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowAddTeam(false)}>
            <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="add-team-modal">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold text-lg mb-4">Add Team</h3>
                <div className="space-y-3 mb-4">
                  <Input value={newTeam.name} onChange={e => setNewTeam(t => ({ ...t, name: e.target.value }))} placeholder="Team Name *" className="bg-zinc-800 border-zinc-700 text-white" data-testid="team-name-input" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newTeam.age_group} onChange={e => setNewTeam(t => ({ ...t, age_group: e.target.value }))} placeholder="Age Group (e.g. 14U)" className="bg-zinc-800 border-zinc-700 text-white" />
                    <Input value={newTeam.season} onChange={e => setNewTeam(t => ({ ...t, season: e.target.value }))} placeholder="Season" className="bg-zinc-800 border-zinc-700 text-white" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddTeam(false)} className="border-zinc-700 text-zinc-400">Cancel</Button>
                  <Button onClick={addTeam} className="text-white" style={{ backgroundColor: color }} data-testid="submit-team-btn">Create Team</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramDashboard;
